/**
 * Send a message to one contact, or to a selection of them.
 *
 * ONE DIALOG, TWO SHAPES, because the decisions are the same ones: which
 * channel, which template or what text, and — once a deployment has more than
 * one number — which number it leaves from. A second dialog for "the same
 * thing but to more people" is how the two come to disagree about the
 * suppression rules.
 *
 * WHAT DIFFERS IS WHAT IT WRITES. One contact queues one message. A SELECTION
 * creates a CAMPAIGN, which is ADMIN-only and deliberately so: the blast
 * radius of a mistake is the difference between one awkward message and a
 * banned business number (CLAUDE.md §8e). The campaign is also what makes a
 * bulk send reviewable afterwards — a frozen recipient list, a cancel path
 * while it drains, and counters in the Outbox — none of which a loop of
 * single sends would leave behind.
 *
 * THE PREVIEW IS NOT COSMETIC. It runs the server's own suppression rules —
 * `previewSend` calls the same `decide` a real send calls — and shows the
 * reason the server WOULD give. So somebody about to tick «ارسال با وجود
 * هشدار» can read what they are overriding rather than guess.
 *
 * And what they can override is exactly one thing: a DUPLICATE. Opt-out is
 * never overridable, here or anywhere, which is why the hint says so.
 *
 * TWO CHANNELS, ONE PATH. Both go through the messaging service — queue,
 * suppression chain, preview, Outbox — and differ only where the media do:
 * an SMS has no picture and no WhatsApp number to leave from, and nothing
 * reports its delivery. SMS goes out through Parsina's Kavenegar service
 * (CLAUDE.md §5b), and whether this deployment HAS that route is the server's
 * answer (`smsStatus`), never assumed here. With no route the tab shows the
 * قالب پیامک, says so, and its send button is disabled; with the sandbox it
 * says «آزمایشی». «sandbox» and «sent» must never be confusable — and neither
 * must «ارسال شد» and «راه‌اندازی نشده». A button that quietly did nothing
 * would be the worse failure by far.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Badge, Button } from '@meshhub/design-tokens/ui';
import React, { useEffect, useRef, useState } from 'react';
import {
  CheckboxField,
  FormDialog,
  SelectField,
  TextArea,
  TextField,
} from '../FormKit.web.jsx';
import {
  CREATE_CAMPAIGN,
  PREVIEW_SEND_QUERY,
  SCHEDULE_CAMPAIGN,
  SEND_MESSAGE,
  SMS_STATUS_QUERY,
  TEMPLATES_QUERY,
  VALIDATE_PLACEHOLDERS,
  WHATSAPP_ACCOUNTS_QUERY,
} from '../../lib/queries.js';
import {
  ALLOWED_PLACEHOLDERS,
  insertAtSelection,
  placeholderToken,
} from '../../lib/placeholders.js';
import { suppressionLabel } from '../../screens/messaging/Outbox.web.jsx';
import { S } from '../../lib/strings.js';
import { PREVIEW_DEBOUNCE_MS } from '../../constants/density.js';
import { formatCount, formatDate } from '../../lib/format.js';
import { MediaPicker } from './MediaPicker.web.jsx';

/**
 * The transports MeshHub itself has, keyed by COMMUNICATION_CHANNEL code.
 *
 * A code and not a label, so a deployment renaming «واتساپ» changes nothing
 * here — the same contract `ChannelIcons` already keeps. Whether a channel is
 * SENDABLE is decided per render from the server: WhatsApp always is (its
 * sandbox is labelled elsewhere), SMS only when `smsStatus` says a route
 * exists.
 */
const CHANNELS = [
  { code: 'WHATSAPP', label: () => S.messaging.whatsapp, templateLabel: () => S.messaging.whatsappTemplate },
  { code: 'SMS', label: () => S.messaging.sms, templateLabel: () => S.messaging.smsTemplate },
];

export function SendMessageDialog({
  open,
  onOpenChange,
  contact,
  onSent,
  /** Many-recipient mode: a BulkSelector ({ contactIds } | { filter }). */
  selector,
  /** How many that selector matches, for the title and the confirmation. */
  recipientCount,
  /**
   * A contact to preview AGAINST in many-recipient mode. The rendered body is
   * per person — «{{firstName}} عزیز» is a different string for each — so the
   * preview shows one real example and says so, rather than a rendering that
   * belongs to nobody.
   */
  sampleContact,
}) {
  const many = Boolean(selector);
  const previewContact = many ? sampleContact : contact;
  const [channelCode, setChannelCode] = useState(CHANNELS[0].code);
  const [templateId, setTemplateId] = useState('');
  const [freeBody, setFreeBody] = useState('');
  const [force, setForce] = useState(false);
  // Which number to send from. '' means the deployment's default, resolved by
  // the server — never computed here, because a second implementation of the
  // resolution order is how a preview and a send come to disagree.
  const [accountId, setAccountId] = useState('');
  // The campaign's name, which is what the Outbox lists it under. Prefilled so
  // the common case is one click, editable because a list of «ارسال گروهی»
  // rows is not something anybody can read back later.
  const [campaignName, setCampaignName] = useState('');
  // A picture or a video, as the ATTACHMENT it was uploaded as. Only its id
  // leaves this component; the BFF turns it into something the messaging
  // service can fetch at send time.
  const [media, setMedia] = useState(null);
  const [mediaBusy, setMediaBusy] = useState(false);

  // SKIPPED WHILE CLOSED, like the accounts below. Failing it leaves SMS
  // unsendable — the safe reading of «we could not ask».
  const { data: smsData } = useQuery(SMS_STATUS_QUERY, {
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  const sms = smsData?.smsStatus;
  const found = CHANNELS.find((c) => c.code === channelCode) ?? CHANNELS[0];
  const isWhatsapp = found.code === 'WHATSAPP';
  const channel = { ...found, sendable: isWhatsapp || Boolean(sms?.configured) };

  const { data: templateData } = useQuery(TEMPLATES_QUERY, {
    variables: { includeInactive: false },
    errorPolicy: 'ignore',
  });
  // The body the PREVIEW asks about, one debounce behind the box. Typing is
  // the only input here that changes on every keystroke; a template is a click.
  const [settledBody, setSettledBody] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSettledBody(freeBody), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [freeBody]);

  const previewBody = templateId ? null : settledBody.trim() || null;

  // PLACEHOLDERS IN FREE TEXT. «سلام {{fullName}}» is rendered per recipient
  // by the server — the same `render` a template goes through — which is what
  // makes a batch send greet forty doctors by forty names. The chips insert a
  // token at the CARET (typing braces into an RTL box is where people give
  // up), and whether what was typed is a known placeholder is the SERVER's
  // answer: the renderer blanks an unknown one rather than leaking braces to
  // a doctor, so «{{fullname}}» would go out as «سلام » with nothing on this
  // screen saying why. Asked only once a brace is typed; a body without one
  // has nothing to validate.
  const bodyRef = useRef(null);
  const { data: placeholderData } = useQuery(VALIDATE_PLACEHOLDERS, {
    variables: { body: settledBody },
    skip: !open || Boolean(templateId) || !settledBody.includes('{{'),
    errorPolicy: 'ignore',
  });
  const placeholderCheck = placeholderData?.validatePlaceholders ?? null;
  const allowedPlaceholders = placeholderCheck?.allowed ?? ALLOWED_PLACEHOLDERS;
  const insertPlaceholder = (name) => {
    const element = bodyRef.current;
    const { value, caret } = insertAtSelection(element, freeBody, placeholderToken(name));
    setFreeBody(value);
    // The controlled box shows the new value on the NEXT render; the caret is
    // placed after that, or the browser leaves it at the end.
    if (element) {
      requestAnimationFrame(() => {
        element.focus();
        element.setSelectionRange(caret, caret);
      });
    }
  };

  // The LABELS, so the preview can say «شمارهٔ فروش» rather than an opaque id.
  // Failing this query must not break the dialog — a send with no label is
  // still a send — so its absence degrades to showing the id.
  const { data: accountsData } = useQuery(WHATSAPP_ACCOUNTS_QUERY, {
    skip: !open,
    fetchPolicy: 'cache-first',
  });
  const accounts = accountsData?.whatsappAccounts ?? [];

  const { data: previewData, previousData } = useQuery(PREVIEW_SEND_QUERY, {
    variables: {
      contactId: previewContact?.id,
      templateId: templateId || null,
      freeBody: previewBody,
      force,
      accountId: isWhatsapp ? accountId || null : null,
      // The server reads the number FOR THIS CHANNEL, so an SMS preview names
      // the mobile the text would really go to.
      channel: channel.code,
    },
    // Only a sendable channel has a preview to ask for: with no SMS route the
    // server would refuse the question, not answer it.
    skip: !open || !previewContact?.id || !channel.sendable,
    fetchPolicy: 'network-only',
  });
  const [send, sendState] = useMutation(SEND_MESSAGE);
  const [createCampaign, campaignState] = useMutation(CREATE_CAMPAIGN);
  // A CAMPAIGN IS CREATED AS A DRAFT, and the worker only runs campaigns that
  // are SCHEDULED with a due timestamp — so creating one and stopping leaves
  // it sitting there forever under a button that said «ارسال». Scheduling it
  // with no time means «now»: the messaging service stamps a past timestamp
  // and the next dispatch pass picks it up, which keeps ONE run path rather
  // than a second send-now that bypasses the runner.
  const [scheduleCampaign, scheduleState] = useMutation(SCHEDULE_CAMPAIGN);
  const { loading, error } = many
    ? { loading: campaignState.loading || scheduleState.loading,
        error: campaignState.error ?? scheduleState.error }
    : sendState;

  // A default name the moment the dialog opens, so «ارسال» is one click.
  //
  // ONCE PER OPENING, not whenever the field is empty. Keying it on
  // `!campaignName` re-filled the box the instant somebody cleared it, so the
  // name could not be changed at all — typing appended to the default instead
  // of replacing it. A ref, because "has this opening been prefilled" is not
  // something the render needs to know.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!open) {
      prefilled.current = false;
      return;
    }
    if (many && !prefilled.current) {
      prefilled.current = true;
      setCampaignName(
        S.messaging.bulkDefaultName
          .replace('{n}', formatCount(recipientCount ?? 0))
          .replace('{date}', formatDate(new Date().toISOString())),
      );
    }
  }, [open, many, recipientCount]);

  // Templates are filtered by their own channel, so «قالب پیامک» and «قالب
  // پیام واتساپ» are the same list read two ways rather than two lists.
  const templates = (templateData?.messageTemplates ?? []).filter(
    (template) => template.channel?.code === channel.code,
  );
  const chosen = templates.find((template) => template.id === templateId);
  // HOLD THE LAST PREVIEW WHILE THE NEXT ONE LOADS. `network-only` blanks
  // `data` for the duration of every refetch, so reading it directly made the
  // whole panel unmount and remount on each keystroke — reported from the
  // running deployment as the preview opening and closing while typing.
  // `previousData` is what keeps the panel on screen and merely stale, which
  // is what a preview should be between renders.
  // Queried even with nothing drafted, deliberately: OPT_OUT, NO_CHANNEL and
  // CONTACT_DELETED are facts about the CONTACT, and knowing not to bother
  // composing is worth more before the message is written than after.
  const preview = previewData?.previewSend ?? previousData?.previewSend;

  // Only the accounts that can actually take a send. A deactivated number is
  // not offered, matching what the server would resolve.
  const sendableAccounts = accounts.filter((a) => a.isActive);

  function pickChannel(code) {
    setChannelCode(code);
    // A template belongs to one channel, so the previous choice cannot survive
    // the switch — clearing it beats leaving a selection that is not in the
    // list any more.
    setTemplateId('');
    setFreeBody('');
    setForce(false);
    // A picture cannot ride along into a text, and an SMS leaves from no
    // WhatsApp number.
    if (code !== 'WHATSAPP') {
      setMedia(null);
      setAccountId('');
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        many
          ? S.messaging.sendToMany.replace('{n}', formatCount(recipientCount ?? 0))
          : S.messaging.sendTo.replace('{name}', contact?.displayName ?? '')
      }
      submitLabel={
        many
          ? S.messaging.sendToManyAction
          : isWhatsapp
            ? S.messaging.sendViaWhatsapp
            : S.messaging.sendSms
      }
      busy={loading}
      error={error}
      canSubmit={
        // A picture or a video alone is a complete message; its text is only
        // a caption. What is refused is a send with nothing in it — and a
        // send while the file is still uploading, which would otherwise go
        // out as the caption alone.
        Boolean(templateId || freeBody.trim() || media) &&
        !mediaBusy &&
        channel.sendable &&
        (!many || Boolean(campaignName.trim()))
      }
      onSubmit={async () => {
        // A template WINS over free text when both are present, matching the
        // server: `sendMessage` renders the template and ignores freeBody.
        // Sending both and letting the server pick would make the preview the
        // user just read a guess about which one it chose.
        const body = templateId ? null : freeBody.trim() || null;
        if (many) {
          // A CAMPAIGN, not a loop of sends. The recipient list is frozen
          // server-side at creation, the whole thing is cancellable while it
          // drains, and the Outbox can account for it afterwards — none of
          // which N separate mutations would leave behind.
          //
          // `selector` is {contactIds} or {filter}, and the filter form is
          // what «انتخاب همهٔ N مورد منطبق» produces: the client never held
          // those ids, so it sends the question and the BFF resolves it once.
          const created = await createCampaign({
            variables: {
              name: campaignName.trim(),
              templateId: templateId || null,
              freeBody: body,
              contactIds: selector.contactIds ?? null,
              filter: selector.filter ?? null,
              accountId: isWhatsapp ? accountId || null : null,
              media: isWhatsapp && media ? { attachmentId: media.id } : null,
              channel: channel.code,
            },
          });
          // TWO CALLS, and the window between them is real: if this second one
          // fails the campaign exists as a DRAFT and nothing has been sent.
          // That is a safe, visible, recoverable state — it is listed on the
          // campaigns screen with its own «start» control — so the error is
          // surfaced rather than compensated by deleting somebody's audit
          // trail. Same reasoning as the follow-up saga (CLAUDE.md §8b).
          const id = created?.data?.createCampaign?.id;
          if (id) await scheduleCampaign({ variables: { id, scheduledAt: null } });
        } else {
          await send({
            variables: {
              contactId: contact.id,
              templateId: templateId || null,
              freeBody: body,
              force,
              accountId: isWhatsapp ? accountId || null : null,
              media: isWhatsapp && media ? { attachmentId: media.id } : null,
              channel: channel.code,
            },
          });
        }
        setTemplateId('');
        setFreeBody('');
        setForce(false);
        setAccountId('');
        setCampaignName('');
        setMedia(null);
        onOpenChange(false);
        onSent?.();
      }}
    >
      {many && (
        <TextField
          label={S.messaging.campaignName}
          hint={S.messaging.bulkNameHint}
          value={campaignName}
          onChange={(event) => setCampaignName(event.target.value)}
        />
      )}
      <SelectField
        label={S.messaging.channel}
        value={channelCode}
        onChange={(event) => pickChannel(event.target.value)}
      >
        {CHANNELS.map((option) => (
          <option key={option.code} value={option.code}>
            {option.label()}
          </option>
        ))}
      </SelectField>

      {/* WHICH NUMBER IT LEAVES FROM.
          Only once there is a choice to make: on a deployment with one number
          a picker holding one option is a decision nobody has, and the blank
          option already means «the default». Absent for SMS, which leaves
          from the SMS line rather than from any WhatsApp number. */}
      {isWhatsapp && sendableAccounts.length > 1 && (
        <SelectField
          label={S.whatsapp.sendsFrom}
          hint={S.whatsapp.defaultHint}
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
        >
          <option value="">
            {sendableAccounts.find((a) => a.isDefault)?.label ?? S.whatsapp.isDefault}
          </option>
          {sendableAccounts.map((account) => (
            <option key={account.id} value={account.accountId}>
              {account.label}
            </option>
          ))}
        </SelectField>
      )}

      <SelectField
        label={channel.templateLabel()}
        value={templateId}
        onChange={(event) => setTemplateId(event.target.value)}
      >
        <option value="">
          {templates.length === 0 ? S.messaging.noTemplateForChannel : S.app.none}
        </option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </SelectField>

      {/* FREE TEXT, and it is not a lesser path.
          The whole stack has taken `freeBody` since Step 5 — the mutation, the
          preview query, the BFF schema and the messaging service all accept it
          — and this dialog never offered a field for it, so the only way to
          message anybody was a template. That also made the send dialog
          unusable on a deployment whose template projection is behind, which
          is exactly when somebody needs to get one message out by hand.
          Disabled rather than hidden while a template is chosen, so the reason
          the box is inert is visible next to it. */}
      {channel.sendable && (
        <TextArea
          ref={bodyRef}
          label={S.messaging.freeBody}
          hint={templateId ? S.messaging.freeBodyIgnored : S.messaging.freeBodyHint}
          value={freeBody}
          disabled={Boolean(templateId)}
          onChange={(event) => setFreeBody(event.target.value)}
        />
      )}

      {/* The same three chips the template editor offers, so a body typed here
          can use what a template can. Absent while a template is chosen: the
          box above is disabled, and a chip that inserts into a disabled box is
          a control that cannot help. */}
      {channel.sendable && !templateId && (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-text-muted">{S.messaging.placeholders}:</span>
            {allowedPlaceholders.map((name) => (
              <Button
                key={name}
                type="button"
                variant="ghost"
                className="text-xs"
                onClick={() => insertPlaceholder(name)}
              >
                <span dir="ltr">{placeholderToken(name)}</span>
              </Button>
            ))}
          </div>
          <p className="text-xs text-text-muted">{S.messaging.placeholderHint}</p>
          {placeholderCheck && !placeholderCheck.valid && (
            <ul className="flex flex-col gap-1" role="alert">
              {placeholderCheck.errors.map((error) => (
                <li key={`${error.placeholder}-${error.position}`} className="text-xs text-danger">
                  {S.messaging.placeholderUnknown
                    .replace('{token}', placeholderToken(error.placeholder))
                    .replace('{allowed}', allowedPlaceholders.map(placeholderToken).join('، '))}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* A picture or a video. WhatsApp only: a text message has no way to
          carry a file. */}
      {isWhatsapp && (
        <MediaPicker
          value={media}
          onChange={setMedia}
          onBusyChange={setMediaBusy}
          disabled={loading}
        />
      )}

      {/* The SERVER's rendering, including the blank a missing first name
          produces — for SMS too, since both go through the same renderer. */}
      {channel.sendable && preview && (
        <div className="flex flex-col gap-2 rounded border border-border bg-surface-hover p-3">
          <p className="text-sm font-medium">
            {many
              ? S.messaging.previewSample.replace(
                  '{name}',
                  previewContact?.displayName ?? '',
                )
              : S.messaging.preview}
          </p>
          <p className="whitespace-pre-wrap text-sm">
            {preview.renderedBody || (
              <span className="text-text-muted">{S.messaging.previewEmpty}</span>
            )}
          </p>
          <p dir="ltr" className="text-start text-xs text-text-muted">
            {preview.phoneE164 || S.app.none}
          </p>
          {/* WHICH NUMBER IT LEAVES FROM, from the server's own resolution.
              Only shown once this deployment has named more than one: with a
              single number it is noise, and with several it is the difference
              between a message from the sales line and one from support.
              Working it out in the client would be a second implementation of
              the resolution order, which is how a preview and a send come to
              disagree. */}
          {/* Only while the picker is left on «default»: then this line is the
              server telling you which number that resolved to, which is the
              one thing the picker cannot say. Once a number is chosen
              explicitly it would just repeat the control above it. */}
          {isWhatsapp && accounts.length > 1 && !accountId && preview.accountId && (
            <p className="text-xs text-text-muted">
              {S.whatsapp.sendsFrom}: {accountLabel(accounts, preview.accountId)}
            </p>
          )}
          {!preview.wouldSend && (
            <p className="flex items-center gap-2 text-sm text-warning">
              <Badge className="bg-surface text-warning">
                {suppressionLabel(preview.reason, channel.code)}
              </Badge>
              {/* In many-recipient mode this is a fact about the ONE contact
                  being previewed, not about the batch — every recipient is
                  decided again, individually, when the campaign runs. Saying
                  otherwise would read as «this whole send is blocked». */}
              {many ? S.messaging.previewSampleSuppressed : preview.detail}
            </p>
          )}
        </div>
      )}

      {/* WHICH SMS ROUTE, named beside the button like the WhatsApp banner:
          the sandbox and a real text must never look alike. */}
      {!isWhatsapp && channel.sendable && (
        <p
          role="status"
          className={
            sms?.provider === 'fake'
              ? 'rounded border border-warning px-4 py-2 text-sm text-warning'
              : 'text-xs text-text-muted'
          }
        >
          {sms?.provider === 'fake' ? S.messaging.smsSandbox : S.messaging.smsLive}
        </p>
      )}

      {/* No transport, so no preview either — the TEMPLATE's own text, plainly
          labelled as the template rather than as what would be sent. Rendering
          the placeholders here would be a second implementation of the
          server's renderer, and the client's is the one people would trust. */}
      {!channel.sendable && (
        <>
          {chosen && (
            <div className="flex flex-col gap-2 rounded border border-border bg-surface-hover p-3">
              <p className="text-sm font-medium">{S.messaging.smsTemplate}</p>
              <p className="whitespace-pre-wrap text-sm">{chosen.body}</p>
            </div>
          )}
          <p role="status" className="rounded border border-warning px-4 py-2 text-sm text-warning">
            {S.messaging.smsNotConfigured}
          </p>
        </>
      )}

      {/* Offered only when a duplicate is what stands in the way. Showing it
          beside an OPT_OUT would imply it could help, and it cannot. */}
      {channel.sendable && preview && !preview.wouldSend && preview.reason === 'DUPLICATE' && (
        <CheckboxField
          label={S.messaging.force}
          checked={force}
          onChange={(event) => setForce(event.target.checked)}
        />
      )}
      {channel.sendable && <p className="text-xs text-text-muted">{S.messaging.forceHint}</p>}
    </FormDialog>
  );
}

/** The label for an account id, falling back to the id itself.

 *  The fallback matters: an account the list does not carry (a rule pointing
 *  at one since deactivated) must still name something, and an id is a worse
 *  answer than a label but a much better one than a blank. */
function accountLabel(accounts, accountId) {
  return accounts.find((a) => a.accountId === accountId)?.label ?? accountId;
}
